import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button } from '@telus-uds/components-web';

import IotPopupTables from '../../iot/orderDetailsSection/iotPopupTables';
import IotSelectedSummaryTable from '../../iot/orderDetailsSection/iotPopupTables/iotSelectedSummaryTable';
import { ButtonWrapper } from '../../iot/orderDetailsSection/iotPopupTables/css';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';

/**
 * Form field of type `IoTPopupButton`. Renders a button that opens an
 * interactive popup of the preloaded Contentful tables named in
 * `iotPopupTablesName`. Completing a product row inside the popup adds it to
 * this field's `completedProducts`, which are shown in a summary table below
 * the button and emitted to the order (with derived monthly / one-time totals)
 * through the field's `onChange` so they flow into `getFormData()`.
 *
 * On resume, the saved value (`data.value.products`) is handed back through
 * `formFields`, so the field re-seeds `completedProducts` from it and re-pushes
 * the derived totals into the order footer (`total.iotPopup[name]`).
 */
const FieldIoTPopupButton = ({ data }) => {
  const {
    name,
    label,
    iotPopupTablesName,
    contentfulData,
    onChange,
    setTotal,
    isRenewal
  } = data;

  const localizedStrings = useLocalizedStringsContext();
  const strings = _.get(localizedStrings, 'iotDynamicTable', {});

  const buttonLabel =
    label || _.get(strings, 'iotPopupButton.buttonLabel', 'View tables');
  // The popup header mirrors the button's label.
  const heading = buttonLabel;
  const emptyTableMessage = _.get(
    strings,
    'iotPopupTables.emptyTableMessage',
    ''
  );
  const doneLabel = _.get(strings, 'iotPopupTables.doneLabel', 'Done');

  const [isOpen, setIsOpen] = useState(false);
  const savedProducts = _.get(data, 'value.products', []);
  const [completedProducts, setCompletedProducts] = useState(
    Array.isArray(savedProducts) ? savedProducts : []
  );
  const buttonRef = useRef(null);

  // `iotPopupTablesName` may be authored either as a flat array of table names
  // or as an object `{ iotPopupTablesName: [...], view: [...] }`; support both.
  const nestedTableNames = _.get(iotPopupTablesName, 'iotPopupTablesName', []);
  let tableNames = [];
  if (Array.isArray(iotPopupTablesName)) {
    tableNames = iotPopupTablesName;
  } else if (Array.isArray(nestedTableNames)) {
    tableNames = nestedTableNames;
  }

  const computeTotals = products =>
    products.reduce(
      (accumulator, product) => ({
        monthly: accumulator.monthly + (_.get(product, 'monthly', 0) || 0),
        oneTime: accumulator.oneTime + (_.get(product, 'oneTime', 0) || 0)
      }),
      { monthly: 0, oneTime: 0 }
    );

  // Restore the footer contribution (`total.iotPopup[name]`) once on resume;
  // the persisted products already live in `formFields`, but the footer totals
  // are derived from `total`, which is not itself rehydrated.
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (didHydrateRef.current) return;
    didHydrateRef.current = true;
    if (completedProducts.length > 0 && setTotal && name) {
      const totals = computeTotals(completedProducts);
      setTotal(previous => ({
        ...previous,
        iotPopup: {
          ...previous.iotPopup,
          [name]: { monthly: totals.monthly, oneTime: totals.oneTime }
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When this field unmounts (e.g. hidden because the current view is not in
  // its configured `view` list), withdraw its footer contribution so a hidden
  // field's products no longer count toward the order totals. It is re-added on
  // remount by the hydration effect above.
  useEffect(
    () => () => {
      if (setTotal && name) {
        setTotal(previous => {
          const nextIotPopup = { ...previous.iotPopup };
          delete nextIotPopup[name];
          return { ...previous, iotPopup: nextIotPopup };
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Keep the committed products in sync with the field's persisted value so an
  // external clear (e.g. switching form views resets the order) empties the
  // summary here too. Keyed on the serialized products so it only runs on an
  // actual content change, not on every parent re-render.
  const savedProductsSignature = JSON.stringify(savedProducts);
  useEffect(() => {
    const nextProducts = _.get(data, 'value.products', []);
    setCompletedProducts(Array.isArray(nextProducts) ? nextProducts : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProductsSignature]);

  const emitToOrder = products => {
    if (!onChange || !name) return;
    const totals = computeTotals(products);
    onChange({ target: { name, value: { products, ...totals } } });
    // Feed the order footer (Due Monthly / Due Now) which is derived from the
    // form's `total` state, not from `formFields`. Keyed by field `name` so
    // multiple IoTPopupButton fields on the same step don't overwrite each
    // other's contribution.
    if (setTotal) {
      setTotal(previous => ({
        ...previous,
        iotPopup: {
          ...previous.iotPopup,
          [name]: { monthly: totals.monthly, oneTime: totals.oneTime }
        }
      }));
    }
  };

  const handleProductsChange = nextProducts => {
    setCompletedProducts(nextProducts);
    emitToOrder(nextProducts);
  };

  const handleRemove = productKey => {
    handleProductsChange(
      completedProducts.filter(product => product.key !== productKey)
    );
  };

  return (
    <>
      <ButtonWrapper ref={buttonRef}>
        <Button
          variant={{ priority: 'low' }}
          onPress={() => setIsOpen(true)}
        >
          {buttonLabel}
        </Button>
      </ButtonWrapper>
      <IotPopupTables
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        heading={heading}
        tableNames={tableNames}
        contentfulData={contentfulData}
        emptyTableMessage={emptyTableMessage}
        doneLabel={doneLabel}
        completedProducts={completedProducts}
        onProductsChange={handleProductsChange}
        focusElementAfterClose={buttonRef}
        isRenewal={isRenewal}
      />
      <IotSelectedSummaryTable
        products={completedProducts}
        onRemove={handleRemove}
      />
    </>
  );
};

FieldIoTPopupButton.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    iotPopupTablesName: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.shape({})
    ]),
    contentfulData: PropTypes.shape({}),
    onChange: PropTypes.func,
    setTotal: PropTypes.func,
    isRenewal: PropTypes.bool
  }).isRequired
};

export default FieldIoTPopupButton;
